import React from 'react';
import PropTypes from 'prop-types';
import {Autocomplete, createFilterOptions} from "@material-ui/lab";
import {TextField, Chip} from "@material-ui/core";
import {getOptionsType} from './optionTypes';
import {formatName, sortArray} from '../../utils';
import removeCircle from '../../svgs/removeCircle.svg';
import arrow from '../../svgs/Arrow.svg';
import close from '../../svgs/X.svg';
import Input from "../Input-new/Input";

require('./Dropdown.scss');

const defaultFilterOptions = createFilterOptions();
const percentDecimals = 2;

const Dropdown = ({dropdownType = 'single', disablePortal = false, inputMode = false, options, optionsType, optionKey, optionLabelKey, value, valueKey, percentKey, firmName, label, percentLabel, data, hasAgentPercent, handleParentChange, handleInputParentChange, disabled = false, percentDisabled = false, viewMode = false, groupBy = false, addNewItem, withAddNew, isFilter = false, theme = 'default', placeholder, hasDrawer, isFromProperty = false, deleted = false, typeKey, typeValue, fillText=null, getSelectedOption, className = '', viewClassName = '', id, index, isOffMarket=false, tooltip, tooltipPosition='up', handleInputFocus, openOnFocus=false, required, customValues=false, onInputChange, inputValue, loading, loadingText, noOptionsText, disableClientFilter=false, onSelectedOptionsChange, viewModeExtra, renderOption, isOptionDisabled, ListboxProps}) => {
    let optionList = options ? options : getOptionsType(optionsType);
    // Filter out invalid options and normalize strings to objects
    // This must happen before passing to Autocomplete to prevent PropTypes warnings and Material-UI errors
    if (!Array.isArray(optionList)) {
        optionList = [];
    } else {
        optionList = optionList.map(opt => {
            // Convert strings to objects for PropTypes compliance
            if (typeof opt === 'string') {
                return { value: opt, label: opt };
            }
            return opt;
        }).filter(opt => {
            // Only allow plain objects (not arrays, null, undefined, or React elements)
            return opt !== null && 
                   opt !== undefined && 
                   typeof opt === 'object' && 
                   !Array.isArray(opt) && 
                   (!opt.type || opt.type !== 'div');
        });
    }

    const getOptionLabel = (option) => {
        // A string only reaches here as a typed-in value on a customValues
        // dropdown; everywhere else the options are objects.
        if (typeof option === 'string') {
            return customValues ? option : '';
        }
        if (option === null || option === undefined || Array.isArray(option)) {
            return '';
        }

        // Note: React elements (div) should not reach here as they're filtered from optionList
        // But if they do, return empty string (Material-UI expects strings, not React elements)
        if (option && option.type === 'div')
            return '';

        switch (optionsType) {
            case 'agent':
            case 'agent-all':
            case 'borrower':
            case 'buyer':
            case 'seller':
            case 'investor':
                const investorName = formatName(option);
                const investorFirmName = option && option.FIRM_NAME && option.FIRM_NAME.trim();
                return investorFirmName ? `${investorName || ''} - ${investorFirmName}` : (investorName || '');
            case 'team':
                const formatted = formatName(option);
                return formatted || '';
            case 'firm':
            case 'tenant':
            case 'lender':
            case 'hsc-followup':
                return option && option.NAME ? option.NAME : '';
            case 'title-company':
                if (typeof option === 'string') {
                    return (option.length && option!=='Other') ? `${option.slice(0, 70)}...` : option;
                }
                return '';
            default:
                if(option==='UPLOADS'){
                    return 'UPLOAD';
                }
                if (typeof option === 'object' && option !== null) {
                    // Handle converted string objects (from territoryProductType, etc.)
                    if (option.label !== undefined) {
                        return String(option.label || '');
                    }
                    if (option.value !== undefined) {
                        return String(option.value || '');
                    }
                    const label = optionLabelKey ? (option[optionLabelKey] || '') : optionKey ? (option[optionKey] || '') : '';
                    return typeof label === 'string' ? label : String(label || '');
                }
                return option ? String(option) : '';
        }
    };

    const getValuesForDropdown = (data, options, optionKey, type = 'multiple') => {
        if (!options || !options.length) {
            return type === 'single' ? null : [];
        }

        const extractValue = (option) => {
            if (!option || typeof option !== 'object') return option;
            if (optionKey && option[optionKey] !== undefined) return option[optionKey];
            if (option.value !== undefined) return option.value;
            return option;
        };

        const normalizedValues = (() => {
            if (data === null || data === undefined) return [];
            if (Array.isArray(data)) return data.map((v) => String(extractValue(v)));
            if (typeof data === 'object') {
                if (optionKey && data[optionKey] !== undefined) return [String(data[optionKey])];
                if (data.value !== undefined) return [String(data.value)];
                if (data.ARCIMEDES_ROW_ID !== undefined) return [String(data.ARCIMEDES_ROW_ID)];
                return [];
            }
            return String(data)
                .split(',')
                .map((v) => v.trim())
                .filter(Boolean);
        })();

        if (!normalizedValues.length) {
            return type === 'single' ? null : [];
        }

        const valueSet = new Set(normalizedValues);
        let response = options.filter((el) => {
            const elValue = extractValue(el);
            if (elValue === undefined || elValue === null) return false;
            if (typeKey && el.groupedBy !== typeValue) return false;
            return valueSet.has(String(elValue));
        });

        if (!response) response = [];

        if (type === "single"){
            response = response && response.length ? response[0] : null;
        }
        if (getSelectedOption)
            getSelectedOption(response);
        // A typed-in value that matches no option is still the value: hand it to
        // the Autocomplete as-is so it shows, instead of a blank field.
        if (type === 'single' && response === null && customValues && typeof data !== 'object' && normalizedValues.length === 1) {
            return normalizedValues[0];
        }
        return response;
    };

    const handleChangeDropdown = ({reason, valueKey, dropdownType = 'single', optionKey = null, alphabetic = false, parentData, isFilter = false}) => {
        const previosData = {...parentData};
        const data = {...parentData};

        if (isFromProperty) {
            if (reason && ['LISTING_AGENT_4', 'BUYER_AGENT_4', 'REFERRING_LISTING_AGENT', 'REFERRING_BUYER_AGENT'].includes(valueKey))
                data[valueKey + '_FIRM'] = reason.FIRM;

            if (['LISTING_AGENT_4', 'BUYER_AGENT_4', 'REFERRING_LISTING_AGENT', 'REFERRING_BUYER_AGENT'].includes(valueKey) && !reason)
                data[valueKey + '_FIRM'] = null;
        }

        if (!reason) {
            if (isFilter) delete data[valueKey];
            else {
                data[valueKey] = null;
                if (['agent-all', 'borrower'].includes(optionsType))
                    data[typeKey] = null;
                if (optionsType === 'agent' && typeKey)
                    data[typeKey] = null;
            }
        } else {
            if (dropdownType === 'single') {
                // Handle converted string objects (from territoryProductType, etc.)
                if (optionKey) {
                    data[valueKey] = reason[optionKey];
                } else if (reason && reason.value !== undefined) {
                    data[valueKey] = reason.value;
                } else {
                    data[valueKey] = reason;
                }
                if (['agent-all', 'borrower'].includes(optionsType))
                    data[typeKey] = reason.groupedBy;
                if (optionsType === 'agent' && typeKey)
                    data[typeKey] = reason.groupedBy != null ? reason.groupedBy : 'oa';
            } else {
                let result = '';

                if (alphabetic) reason = sortArray(reason, (optionKey ? 'name' : null));

                reason.forEach((el, index) => {
                    // Handle converted string objects
                    const elValue = optionKey ? el[optionKey] : (el.value !== undefined ? el.value : el);
                    result += (index !== 0 ? ',' : '') + elValue;
                });

                if (alphabetic) data[valueKey] = result.slice(0, -1);
                else data[valueKey] = result;
            }
        }

        if (typeof onSelectedOptionsChange === 'function') {
            onSelectedOptionsChange(dropdownType === 'multiple' ? (reason || []) : reason);
        }

        handleParentChange(data, optionsType, (reason ? reason[optionKey] : ''), valueKey, typeKey, reason, previosData);
    };

    const handleChange = (key, value) => {
        handleInputParentChange(key, value);
    };

    const filterOptions = (options, state) => {
        if (disableClientFilter) {
            // Remote-search dropdowns already filter server-side; do not filter again in the UI.
            return options ? options.filter(opt => opt && typeof opt === 'object' && !Array.isArray(opt) && opt.type !== 'div') : [];
        }
        // Filter out invalid options (empty arrays, strings, null, undefined)
        const validOptions = options ? options.filter(opt => opt && typeof opt === 'object' && !Array.isArray(opt) && opt.type !== 'div') : [];
        const results = defaultFilterOptions(validOptions, state);
        if (optionsType === 'firm')
            validOptions.forEach((option, index) => {
                if (!Object.keys(option).length)
                    validOptions.splice(index, 1);
            });

        if (['agent', 'buyer', 'seller', 'firm', 'lender', 'agent-all', 'borrower', 'owner'].includes(optionsType) && !results.length && withAddNew)
            return [
                <div className='no-options-container' onClick={(e) => e.stopPropagation()}>
                    No results found
                    <div className='add-new'
                         onClick={(e) => {
                             if (optionsType === 'firm' && hasDrawer) addNewItem();
                             else if (optionsType === 'borrower') addNewItem(valueKey);
                             else addNewItem(valueKey, 'buyer', index);

                             const popper = document.getElementsByClassName('MuiAutocomplete-popper');
                             if (popper && popper[0]) popper[0].style.display = 'none';

                             e.stopPropagation();
                         }}>Add new {optionsType === 'agent-all' ? 'agent' :
                        optionsType === 'borrower' ? 'buyer' : optionsType}
                    </div>
                    {
                        optionsType === 'borrower' &&
                        <div className='add-new'
                             onClick={(e) => {
                                 addNewItem(valueKey, 'seller', index);

                                 const popper = document.getElementsByClassName('MuiAutocomplete-popper');
                                 if (popper && popper[0]) popper[0].style.display = 'none';

                                 e.stopPropagation();
                             }}>
                            Add new seller
                        </div>
                    }
                </div>
            ];

        if (!results.length && !inputMode)
            return [
                <div className='no-options-container'
                     onClick={(e) => e.stopPropagation()}>
                    No results found
                </div>
            ];
        return results;
    };

    let values = getValuesForDropdown(value, optionList, optionKey, dropdownType);
    // Ensure values is never an empty array for single dropdowns (Material-UI expects null)
    if (dropdownType === 'single' && Array.isArray(values) && values.length === 0) {
        values = null;
    }

    const getFormattedValues = () => {
        if (value && Array.isArray(values) && fillText)
            return values.map(el => getOptionLabel(el) + fillText ? fillText:'').join(', ');
        if (value && Array.isArray(values))
            return values.map(el => getOptionLabel(el)).join(', ');
        if (value && !inputMode)
            return `${getOptionLabel(values)}${fillText ? fillText:''}`;
        if (value  && inputMode){
            return value == 'Interest Only' ? value :`${value}${fillText ? fillText:''}`;
        }
        if (!value && firmName) return firmName;
        return '-';
    };

    const rest = {};
    if (groupBy)
        rest.groupBy = (option) => {
            return option.groupedBy;
        };
    // Custom option rendering (e.g. extra context in the list) without changing
    // how the selected value is displayed (getOptionLabel).
    if (renderOption)
        rest.renderOption = renderOption;
    // The pickers' scroll handler, which loads their next page.
    if (ListboxProps)
        rest.ListboxProps = ListboxProps;

    return (
        <div className={`dropdown-container${viewMode ? ' view-mode' : ''} ${className}`}>
            {
                viewMode && tooltip &&
                <span className={`tooltip-new ${tooltipPosition ? tooltipPosition:''}`}>{tooltip}</span>
            }
            {
                !viewMode &&
                <Autocomplete className={theme + (dropdownType === 'multiple' ? ' multiple' : '')}
                              multiple={dropdownType === 'multiple'}
                              disabled={disabled}
                              disablePortal={disablePortal}
                              fullWidth
                              getOptionLabel={(option) => {
                                  // Material-UI might pass arrays, null, or other invalid values during internal processing - handle gracefully
                                  if (option === null || option === undefined || Array.isArray(option)) {
                                      return '';
                                  }
                                  return getOptionLabel(option);
                              }}
                              options={optionList}
                              onInputChange={onInputChange}
                              inputValue={inputValue}
                              loading={loading}
                              loadingText={loadingText}
                              noOptionsText={noOptionsText}
                              getOptionDisabled={(option) => (firmName != null && (option.NAME === firmName || option.NAME === "  – " + firmName)) || (!!isOptionDisabled && isOptionDisabled(option))}
                              value={values}                              
                              openOnFocus={openOnFocus}
                              id={id}
                              onFocus={handleInputFocus}
                              renderInput={(params) =>
                                  <TextField {...params}
                                             label={label}
                                             required={required}
                                             placeholder={placeholder}
                                             variant="outlined"
                                             inputProps={{
                                                 ...(params && params.inputProps ? params.inputProps : {}),
                                                 onChange: inputMode
                                                     ? (event) => {
                                                         // Preserve Material-UI's internal handler so typing works.
                                                         if (params && params.inputProps && typeof params.inputProps.onChange === 'function') {
                                                             params.inputProps.onChange(event);
                                                         }
                                                         values = event.target.value;
                                                         handleChangeDropdown({
                                                             reason: event.target.value,
                                                             valueKey,
                                                             dropdownType,
                                                             optionKey,
                                                             parentData: data,
                                                             isFilter
                                                         })
                                                     }
                                                     : (params && params.inputProps ? params.inputProps.onChange : undefined),
                                             }}
                                  />}
                              renderTags={(tagValue, getTagProps) =>
                                  tagValue.map((option, index) =>
                                      <Chip key={index}
                                            {...getTagProps({index})}
                                            deleteIcon={<div className='chip-remove-icon' dangerouslySetInnerHTML={{__html: removeCircle}}/>}
                                            label={getOptionLabel(option)}/>
                                  )}
                              onChange={(event, selectedValue, reason) => {
                                  // Material-UI Autocomplete onChange: (event, value, reason)
                                  // In inputMode, only handle actual user selections, not blur events
                                  if (inputMode) {
                                      // Only process when user selects an option or clears the field
                                      // Ignore 'blur' and other automated triggers
                                      // Note: Material-UI uses 'select-option' (kebab-case), not 'selectOption'
                                      if (reason === 'select-option' || reason === 'clear') {
                                          handleChangeDropdown({reason: selectedValue, valueKey, dropdownType, optionKey, parentData: data, isFilter});
                                      }
                                  } else {
                                      handleChangeDropdown({reason: selectedValue, valueKey, dropdownType, optionKey, parentData: data, isFilter});
                                  }
                              }}
                              popupIcon={<div className='dropdown-icon arrow' dangerouslySetInnerHTML={{__html: arrow}}/>}
                              closeIcon={<div className='dropdown-icon' dangerouslySetInnerHTML={{__html: close}}/>}
                              filterOptions={(options, state) => filterOptions(options, state)}
                              freeSolo={customValues ? true : false}
                              {...rest}
                />
            }
            {
                viewMode &&
                <div className={`view-mode-container ${viewClassName}`}>
                    {
                        (!['status'].includes(optionsType) || isOffMarket || 'null')&&
                        <label className='view-mode-label'>{label}</label>
                    }
                    <div className={deleted ? "view-mode-deleted" : ""} style={viewModeExtra ? { display: 'flex', alignItems: 'center', gap: '8px' } : undefined}>
                        {
                            ['status'].includes(optionsType) && !label &&
                            <span className='view-mode-label status'>Status</span>
                        }
                        {getFormattedValues()}
                        {viewModeExtra}
                    </div>
                </div>
            }
            {
                hasAgentPercent &&
                <div className={`agent-percent-container${percentLabel ? ' has-label' : '' }`}>
                    <Input type='text'
                           valueType='agent-percent'
                           name={percentKey}
                           value={data[percentKey] || ''}
                           label={percentLabel}
                           viewMode={viewMode}
                           deleted={deleted}
                           disabled={disabled || percentDisabled}
                           decimals={percentDecimals}
                           handleParentChange={handleChange}
                    />
                </div>
            }
        </div>
    )
};

Dropdown.propTypes = {
    id: PropTypes.string,
    dropdownType: PropTypes.oneOf(['single', 'multiple']),
    options: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.object),
        PropTypes.arrayOf(PropTypes.string)
    ]),
    optionsType: PropTypes.oneOf(['status', 'source', 'states', 'countries', 'type', 'transaction', 'category', 'lease', 'market', 'buyer-market', 'subcategorySTNL', 'subcategoryMT', 'subcategoryIndustrialSTNLMT', 'singleTenantOffice', 'multiTenantOffice', 'superUsers', 'profiles', 'buyerStatus', 'agent', 'buyer', 'firm', 'seller', 'team', 'tenant', 'lender', 'checkbox', 'status-financing', 'loan', 'agent-all', 'borrower', 'filters','team-members', 'sellerStatus','loanquotes-amortization','estimatedPrice', 'referralType', 'confirmType','roles','territory','hsc-followup', 'territory', 'productType', 'territoryMarket','title-companies', 'psa-modifications','vendorType', 'vendorSubtype', 'investor', 'lender-contact', 'lenderLoanType']).isRequired,
    optionKey: PropTypes.string,
    optionLabelKey: PropTypes.string,
    value: PropTypes.string,
    valueKey: PropTypes.string.isRequired,
    percentKey: PropTypes.string,
    firmName: PropTypes.string,
    label: PropTypes.string,
    percentLabel: PropTypes.string,
    data: PropTypes.object,
    hasAgentPercent: PropTypes.bool,
    handleParentChange: PropTypes.func.isRequired,
    handleInputParentChange: PropTypes.func,
    disabled: PropTypes.bool,
    isOptionDisabled: PropTypes.func,
    /** Lock only the agent % box next to the dropdown (access levels). */
    percentDisabled: PropTypes.bool,
    viewMode: PropTypes.bool,
    addNewItem: PropTypes.func,
    withAddNew: PropTypes.bool,
    isFilter: PropTypes.bool,
    groupBy: PropTypes.bool,
    theme: PropTypes.oneOf(['default', 'white', 'dark', 'tracking', 'no-border']),
    placeholder: PropTypes.string,
    hasDrawer: PropTypes.bool,
    deleted: PropTypes.bool,
    typeKey: PropTypes.string,
    typeValue: PropTypes.string,
    getSelectedOption: PropTypes.func,
    className: PropTypes.string,
    viewClassName: PropTypes.string,
    fillText: PropTypes.string,
    index: PropTypes.number,
    tooltip: PropTypes.string,    
    tooltipPosition: PropTypes.oneOf(['up','down']),
    handleInputFocus: PropTypes.func,
    openOnFocus: PropTypes.bool,
    onInputChange: PropTypes.func,
    inputValue: PropTypes.string,
    loading: PropTypes.bool,
    loadingText: PropTypes.string,
    noOptionsText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    disableClientFilter: PropTypes.bool,
    onSelectedOptionsChange: PropTypes.func,
};

export default Dropdown;